import { Button, Group, Stack, TextInput } from "@mantine/core";
import { Form, useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { zodResolver } from "mantine-form-zod-resolver";
import { GeoLayer } from "shared/components/organization/geolocations/types";
import { GEO_QUERY_KEY } from "shared/components/organization/geolocations/useGeoLocations";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { queryClient } from "src/queryClient";
import { z } from "zod";

const editLayerFormSchema = z.object({
    name: z.string().trim().min(1, "Name is required"),
});

type EditLayerFormProps = {
    layer: GeoLayer;
    onCancel: () => void;
};

export const EditLayerForm = ({ layer, onCancel }: EditLayerFormProps) => {
    const updateLayer = useGraphqlMutation(muts.updateLayer);

    const form = useForm({
        initialValues: { name: layer.name },
        validate: zodResolver(editLayerFormSchema),
    });

    function handleSubmit(values: typeof form.values) {
        const name = values.name.trim();
        if (!name) return;

        void updateLayer
            .mutateAsync({ id: layer.id, name })
            .then(() => {
                notify.show.success("Layer updated");
                queryClient.setQueryData<GeoLayer[]>(GEO_QUERY_KEY, (old) =>
                    old ? old.map((l) => (l.id === layer.id ? { ...l, name } : l)) : old,
                );
                modals.closeAll();
            })
            .catch(notify.catch);
    }

    return (
        <Form form={form} onSubmit={handleSubmit}>
            <Stack>
                <TextInput label="Name" withAsterisk data-autofocus {...form.getInputProps("name")} />
                <Group justify="flex-end">
                    <Button variant="default" onClick={onCancel} disabled={updateLayer.isPending}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        loading={updateLayer.isPending}
                        disabled={!form.values.name.trim() || form.values.name === layer.name}
                    >
                        Save
                    </Button>
                </Group>
            </Stack>
        </Form>
    );
};

const muts = {
    updateLayer: graphql(`
        mutation UpdateGeoLayer($id: UUID!, $name: String!) {
            updateGeoLayer(id: $id, parameters: { name: $name }) {
                id
            }
        }
    `),
};
